/**
 * Evolução da Roda — gráfico de linhas com a progressão das 8 áreas
 * ao longo de todas as avaliações do utilizador.
 */
import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";

const AREA_COLORS = {
  "Saúde": "#4A5E2E",
  "Família": "#0F1829",
  "Amor": "#B91C1C",
  "Carreira": "#1D4ED8",
  "Finanças": "#C2831A",
  "Desenvolvimento Pessoal": "#7C3AED",
  "Lazer": "#0D9488",
  "Espiritualidade": "#978D78",
};
const MEDIA_KEY = "Média";

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 640;

export default function RodaEvolucao({ items }) {
  const [hidden, setHidden] = useState(new Set());

  // Cronológico (mais antiga → mais recente) para leitura natural da evolução.
  const data = useMemo(() => {
    return [...items]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((it) => {
        const row = { data: shortDate(it.created_at) };
        let sum = 0, n = 0;
        Object.keys(AREA_COLORS).forEach((area) => {
          const v = Number(it.scores?.[area] || 0);
          row[area] = v;
          sum += v; n += 1;
        });
        row[MEDIA_KEY] = n ? Number((sum / n).toFixed(1)) : 0;
        return row;
      });
  }, [items]);

  if (items.length < 2) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="coach-evolution-empty">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={18} className="text-[#4A5E2E]" />
          <h3 className="text-sm font-semibold text-[#0F1829]">Evolução da Roda</h3>
        </div>
        <p className="text-sm text-[#6B7280]">
          Faz pelo menos 2 avaliações para veres aqui a evolução das tuas 8
          áreas ao longo do tempo.
        </p>
      </div>
    );
  }

  const toggle = (key) => {
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allKeys = [...Object.keys(AREA_COLORS), MEDIA_KEY];

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="coach-evolution">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={18} className="text-[#4A5E2E]" />
        <h3 className="text-sm font-semibold text-[#0F1829]">
          Evolução da Roda ({items.length} avaliações)
        </h3>
      </div>
      <p className="text-xs text-[#6B7280] mb-3">
        Clica numa área para a mostrar/esconder no gráfico.
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {allKeys.map((key) => {
          const off = hidden.has(key);
          const color = key === MEDIA_KEY ? "#111827" : AREA_COLORS[key];
          return (
            <button
              key={key}
              onClick={() => toggle(key)}
              className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${
                off
                  ? "border-[#E5E7EB] text-[#9CA3AF] bg-white"
                  : "text-white border-transparent"
              }`}
              style={off ? {} : { backgroundColor: color }}
              data-testid={`coach-evolution-toggle-${key}`}
            >
              {key === "Desenvolvimento Pessoal" ? "Desenv. Pessoal" : key}
            </button>
          );
        })}
      </div>

      <div className="w-full h-[260px] sm:h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 8, bottom: 5, left: -22 }}>
            <CartesianGrid stroke="#F3F4F6" />
            <XAxis
              dataKey="data"
              tick={{ fill: "#6B7280", fontSize: isMobile() ? 9 : 11 }}
              interval={isMobile() ? "preserveStartEnd" : 0}
            />
            <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fill: "#978D78", fontSize: 10 }} />
            <Tooltip
              formatter={(v, name) => [`${v}/10`, name]}
              contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
            />
            {!isMobile() && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {Object.entries(AREA_COLORS).map(([area, color]) =>
              hidden.has(area) ? null : (
                <Line
                  key={area}
                  type="monotone"
                  dataKey={area}
                  name={area === "Desenvolvimento Pessoal" ? "Desenv. Pessoal" : area}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              )
            )}
            {!hidden.has(MEDIA_KEY) && (
              <Line
                type="monotone"
                dataKey={MEDIA_KEY}
                stroke="#111827"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={{ r: 3 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "2-digit" });
}
